import React, { useState, useContext } from 'react';
import { useFirebase } from '../context/firebaseContext';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faLock, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { PlatformContext } from '../context/PlatformContext';
import LoginMobile from '../mobile/components/LoginMobile';

const Login = () => {
    const { platform, loading } = useContext(PlatformContext);
    const { auth } = useFirebase();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const navigate = useNavigate();

    const getErrorMessage = (code) => {
        switch (code) {
            case 'auth/wrong-password':
                return 'Password is incorrect. Try again or reset it if you\'ve forgotten.';
            case 'auth/user-not-found':
                return 'No account exists with that email. Double-check or create one.';
            case 'auth/invalid-email':
                return 'That email doesn\'t look valid. Please update it and try again.';
            default:
                return 'Login failed. Please check your credentials and try again.';
        }
    };

    const handleLogin = async (e) => {
        e.preventDefault();

        try {
            setError(null);
            await signInWithEmailAndPassword(auth, email, password);

            // Navigate to /profile after successful login
            navigate('/profile');
        } catch (error) {
            console.error('Login error:', error.message);
            setError(getErrorMessage(error?.code));
        }
    };

    // Show mobile login on mobile platforms
    if (!loading && (platform === 'mobileApp' || platform === 'mobileWeb')) {
        return <LoginMobile />;
    }

    // Show desktop login on desktop platforms
    return (
        <section className="hero is-dark-grey has-text-centered auth-hero">
            <div className="hero-body">
                <div className="container">
                    <div className="columns is-centered">
                        <div className="column">
                            <form onSubmit={handleLogin} className="box lightning-border" style={{ 
                                maxWidth: '450px', 
                                margin: '0 auto', 
                                padding: '2.5rem',
                                boxShadow: '0 10px 30px rgba(255, 215, 0, 0.2), 0 4px 12px rgba(0, 0, 0, 0.3)',
                                borderRadius: '12px',
                                background: 'linear-gradient(135deg, rgba(119, 44, 56, 0.95), rgba(119, 44, 56, 0.85))'
                            }}>
                                
                                {/* Modern Typography Header */}
                                <div className="has-text-centered mb-5">
                                    <h1 className="title has-text-warning is-size-2 has-text-weight-bold" style={{
                                        color: '#FFA500',
                                        letterSpacing: '0.05em',
                                        textShadow: '0 2px 8px rgba(255, 165, 0, 0.4)',
                                        marginBottom: '0.5rem'
                                    }}>
                                        Welcome Back
                                    </h1>
                                </div>

                                <div className="form-fields">
                                    {/* Email Field with Floating Label and Icon */}
                                    <div className="field mb-4">
                                        <div className="control has-icons-left" style={{ position: 'relative' }}>
                                            <input
                                                className="input has-text-warning"
                                                type="email"
                                                id="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                required
                                                style={{
                                                    paddingLeft: '2.5rem',
                                                    paddingRight: '1rem',
                                                    border: '1px solid rgba(255, 215, 0, 0.3)',
                                                    borderRadius: '8px',
                                                    transition: 'all 0.3s ease',
                                                    background: 'rgba(255, 255, 255, 0.05)',
                                                    backdropFilter: 'blur(10px)'
                                                }}
                                                onFocus={(e) => {
                                                    e.target.style.borderColor = '#FFD700';
                                                    e.target.style.boxShadow = '0 0 0 2px rgba(255, 215, 0, 0.2)';
                                                }}
                                                onBlur={(e) => {
                                                    e.target.style.borderColor = 'rgba(255, 215, 0, 0.3)';
                                                    e.target.style.boxShadow = 'none';
                                                }}
                                            />
                                            <span style={{
                                                position: 'absolute',
                                                left: '0.7rem',
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                color: '#FFD700',
                                                zIndex: 2
                                            }}>
                                                <FontAwesomeIcon icon={faEnvelope} style={{ fontSize: '16px' }} />
                                            </span>
                                        </div>
                                    </div>

                                    {/* Password Field with Floating Label and Icon */}
                                    <div className="field mb-4">
                                        <div className="control has-icons-left" style={{ position: 'relative' }}>
                                            <input
                                                className="input has-text-warning"
                                                type={showPassword ? 'text' : 'password'}
                                                id="password"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                required
                                                style={{
                                                    paddingLeft: '2.5rem',
                                                    paddingRight: '2.5rem',
                                                    border: '1px solid rgba(255, 215, 0, 0.3)',
                                                    borderRadius: '8px',
                                                    transition: 'all 0.3s ease',
                                                    background: 'rgba(255, 255, 255, 0.05)',
                                                    backdropFilter: 'blur(10px)'
                                                }}
                                                onFocus={(e) => {
                                                    e.target.style.borderColor = '#FFD700';
                                                    e.target.style.boxShadow = '0 0 0 2px rgba(255, 215, 0, 0.2)';
                                                }}
                                                onBlur={(e) => {
                                                    e.target.style.borderColor = 'rgba(255, 215, 0, 0.3)';
                                                    e.target.style.boxShadow = 'none';
                                                }}
                                            />
                                            <span style={{
                                                position: 'absolute',
                                                left: '0.7rem',
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                color: '#FFD700',
                                                zIndex: 2
                                            }}>
                                                <FontAwesomeIcon icon={faLock} style={{ fontSize: '16px' }} />
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                style={{
                                                    position: 'absolute',
                                                    right: '0.7rem',
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    background: 'none',
                                                    border: 'none',
                                                    color: '#FFD700',
                                                    cursor: 'pointer',
                                                    zIndex: 2
                                                }}
                                            >
                                                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} style={{ fontSize: '16px' }} />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Remember Me Checkbox */}
                                    <div className="field mb-4">
                                        <div className="control">
                                            <label className="checkbox has-text-warning" style={{ fontSize: '0.9rem' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={rememberMe}
                                                    onChange={(e) => setRememberMe(e.target.checked)}
                                                    style={{ marginRight: '0.5rem' }}
                                                />
                                                Remember me
                                            </label>
                                        </div>
                                    </div>

                                    {/* Error Message */}
                                    {error && (
                                        <div className="auth-alert is-error" role="alert" style={{ marginBottom: '1rem' }}>
                                            <div className="auth-alert__icon" aria-hidden="true">!</div>
                                            <div className="auth-alert__content">
                                                <div className="auth-alert__title">Password issue</div>
                                                <div className="auth-alert__text">{error}</div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Submit Button */}
                                    <div className="field">
                                        <div className="control">
                                            <button
                                                type="submit"
                                                className="button is-warning is-fullwidth"
                                                style={{
                                                    background: 'linear-gradient(135deg, #FFD700, #FFA500)',
                                                    border: 'none',
                                                    borderRadius: '8px',
                                                    padding: '0.75rem 1.5rem',
                                                    fontSize: '1rem',
                                                    fontWeight: '600',
                                                    transition: 'all 0.3s ease',
                                                    boxShadow: '0 4px 15px rgba(255, 215, 0, 0.3)'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.target.style.transform = 'translateY(-2px)';
                                                    e.target.style.boxShadow = '0 6px 20px rgba(255, 215, 0, 0.4)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.style.transform = 'translateY(0)';
                                                    e.target.style.boxShadow = '0 4px 15px rgba(255, 215, 0, 0.3)';
                                                }}
                                            >
                                                Sign In
                                            </button>
                                        </div>
                                    </div>

                                    {/* Links */}
                                    <div className="has-text-centered mt-4">
                                        <p className="has-text-warning" style={{ fontSize: '0.9rem' }}>
                                            Don't have an account?{' '}
                                            <Link to="/register" style={{ color: '#FFA500', textDecoration: 'underline' }}>
                                                Register here
                                            </Link>
                                        </p>
                                        <p className="has-text-warning mt-2" style={{ fontSize: '0.9rem' }}>
                                            <Link to="/forgotpassword" style={{ color: '#FFA500', textDecoration: 'underline' }}>
                                                Forgot your password?
                                            </Link>
                                        </p>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Login;
